import { useAuth0 } from '@auth0/auth0-react';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Col, Container, Row, Tab, Tabs } from 'react-bootstrap';
import Select from 'react-select';
import ActivitiesChart from './partials/ActivitiesChart';
import CompactReportingControls from './CompactReportingControls';
import ConversationCountChart from './ConversationCountChart';
import ResponseCountChart from './partials/ResponseCountChart';
import UserFeedbackChart from './partials/UserFeedbackChart';
import SeriesDropdown from './SeriesDropdown';
import ChannelAdvancedFilters, {
  type AdvancedFilters,
} from './ChannelAdvancedFilters';
import { config } from '../../config';
import SweetLayout from '../../Layout/SweetLayout';
import '../../styles/Reporting.css';

const ACCOUNT_CACHE_KEY = "admin_accounts_cache";

interface Channel {
  channel: string;
  name: string;
}

interface ChannelOption {
  value: string;
  label: string;
}

interface Account {
  account_id: number;
  name: string;
  owner_user_id: string;
}

interface AccountOption {
  value: number | string;
  label: string;
}

const normalizeAccount = (account: any): Account | null => {
  if (!account || typeof account !== "object") {
    return null;
  }
  return {
    account_id: account.account_id ?? account.id,
    name: account.name ?? account.account_name ?? "",
    owner_user_id: account.owner_user_id ?? account.owner ?? account.account_owner ?? "",
  };
};

const readAccountsFromCache = (): Account[] | null => {
  try {
    const cached = sessionStorage.getItem(ACCOUNT_CACHE_KEY);
    if (!cached) {
      return null;
    }
    const parsed = JSON.parse(cached);
    return Array.isArray(parsed) ? parsed.map(normalizeAccount).filter((a): a is Account => a !== null) : null;
  } catch (error) {
    console.warn("Failed to read accounts from cache", error);
    return null;
  }
};

const writeAccountsToCache = (accounts: Account[]): void => {
  try {
    const normalized = Array.isArray(accounts)
      ? accounts.map(normalizeAccount).filter((a): a is Account => a !== null)
      : [];
    sessionStorage.setItem(ACCOUNT_CACHE_KEY, JSON.stringify(normalized));
  } catch (error) {
    console.warn("Failed to write accounts to cache", error);
  }
};

const Reporting: React.FC = () => {
  const { user, getIdTokenClaims } = useAuth0();
  
  // Account state
  const [accounts, setAccounts] = useState<Account[]>(() => {
    const cached = readAccountsFromCache();
    return cached || [];
  });
  const [selectedAccount, setSelectedAccount] = useState<AccountOption | null>(null);
  const [loadingAccounts, setLoadingAccounts] = useState<boolean>(false);
  const hasFetchedAccounts = useRef(false);
  
  // Channel and reporting state
  const [channels, setChannels] = useState<ChannelOption[]>([]);
  const [channel_list, setChannelList] = useState<Channel[]>([]);
  const [filterChannels, setFilterChannels] = useState<string>('');
  const [start_date, setStartDate] = useState<string | null>(null);
  const [end_date, setEndDate] = useState<string | null>(null);
  const [range, setRange] = useState<number | null>(30);
  const [loading, setLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('responses');

  // Series state for responses - default to channels
  const [seriesType, setSeriesType] = useState<string>('channels');
  const [seriesTagType, setSeriesTagType] = useState<string | null>(null);
  const [seriesAssessmentId, setSeriesAssessmentId] = useState<string | null>(null);
  const [responsesReady, setResponsesReady] = useState<boolean>(false);
  const channelsFetchedRef = React.useRef(false);
  
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>({
    tags: {},
    assessments: {},
  });
  const [advancedFiltersDraft, setAdvancedFiltersDraft] = useState<AdvancedFilters>({
    tags: {},
    assessments: {},
  });

  const fetchAccounts = useCallback(async (): Promise<void> => {
    // Check cache first
    const cached = readAccountsFromCache();
    if (cached && cached.length > 0) {
      console.log("Reporting: Using cached accounts", cached.length);
      setAccounts(cached);
      hasFetchedAccounts.current = true;
      return;
    }

    try {
      setLoadingAccounts(true);
      const getidToken = await getIdTokenClaims();
      const idToken = getidToken?.__raw;
      const options = {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      };
      const path = `/account`;
      const url = `${config.baseURL}/admin/v1${path}`;
      const response = await fetch(url, options);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const result = await response.json();
      const normalized = Array.isArray(result)
        ? result.map(normalizeAccount).filter((a): a is Account => a !== null)
        : [];
      setAccounts(normalized);
      writeAccountsToCache(normalized);
      setLoadingAccounts(false);
      hasFetchedAccounts.current = true;
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setLoadingAccounts(false);
    }
  }, [getIdTokenClaims]);

  // Fetch accounts on mount
  useEffect(() => {
    if (user && !hasFetchedAccounts.current && !loadingAccounts) {
      // If accounts already loaded from cache, mark as fetched
      if (accounts.length > 0) {
        hasFetchedAccounts.current = true;
        return;
      }
      fetchAccounts();
    }
  }, [user, loadingAccounts, accounts.length, fetchAccounts]);

  const fetchChannels = useCallback(async (): Promise<void> => {
    try {
      const getidToken = await getIdTokenClaims();
      const idToken = getidToken?.__raw;
      const options = {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      };
      let path = `/channel_list`;
      if (selectedAccount && selectedAccount.value !== 'all') {
        path = `/channel_list?account_id=${selectedAccount.value}`;
      }
      const url = `${config.baseURL}/admin/v1${path}`;
      const response = await fetch(url, options);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const result = await response.json();
      setChannelList(result);
    } catch (error) {
      console.error('Error fetching channels:', error);
    }
  }, [getIdTokenClaims, selectedAccount]);

  // Fetch channels when account is selected
  useEffect(() => {
    if (user && selectedAccount) {
      // Reset the ref when account changes to allow refetch
      channelsFetchedRef.current = false;
      fetchChannels();
      channelsFetchedRef.current = true;
    } else {
      // Clear channel list when no account is selected
      setChannelList([]);
      channelsFetchedRef.current = false;
    }
  }, [user, selectedAccount, fetchChannels]);

  const getReports = async (
    type?: string,
    lookback?: number,
    series?: string,
    channel?: string,
    startDate?: string | null,
    endDate?: string | null,
    downloadType?: string,
    tags?: string | null,
    seriesType?: string | null,
    seriesTagType?: string | null,
    seriesAssessmentId?: string | null
  ): Promise<any> => {
    try {
      const getidToken = await getIdTokenClaims();
      const idToken = getidToken?.__raw;
      const options = {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      };

      const params = new URLSearchParams();

      // data_type: prefer explicit downloadType over type when provided
      if (downloadType) {
        params.set('data_type', String(downloadType));
      } else if (type) {
        params.set('data_type', String(type));
      }

      // lookback only when custom range not used
      if (lookback && !startDate && !endDate) {
        params.set('lookback', String(lookback));
      }

      // series_type: prefer seriesType prop over legacy series
      if (seriesType) {
        params.set('series_type', String(seriesType));
      } else if (series) {
        params.set('series_type', String(series));
      }

      if (seriesTagType) {
        params.set('series_tag_type', String(seriesTagType));
      }
      if (seriesAssessmentId) {
        params.set('series_assessment_id', String(seriesAssessmentId));
      }
      if (channel) {
        params.set('channel_id_list', String(channel));
      }
      if (startDate) {
        params.set('start_date', String(startDate));
      }
      if (endDate) {
        params.set('end_date', String(endDate));
      }
      if (tags) {
        params.set('tags', String(tags));
      }

      // Add account_id parameter
      if (selectedAccount && selectedAccount.value !== 'all') {
        params.set('account_id', String(selectedAccount.value));
      }

      // Advanced filters payload
      const af = advancedFilters;
      if (
        af &&
        (Object.keys(af.tags || {}).length > 0 ||
          Object.keys(af.assessments || {}).length > 0)
      ) {
        params.set('advanced_filters', encodeURIComponent(JSON.stringify(af)));
      }

      const path = `/usage?${params.toString()}`;
      const url = `${config.baseURL}/admin/v1${path}`;
      const response = await fetch(url, options);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(error);
    }
  };

  const handleChannelChange = (selectedOptions: ChannelOption[]): void => {
    setChannels(selectedOptions);
    const options = selectedOptions.map(channel => channel.value).join(',');
    setFilterChannels(options);
  };

  const handleAccountChange = (selectedOption: AccountOption): void => {
    setSelectedAccount(selectedOption);
    // Reset channel selection when account changes
    setChannels([]);
    setFilterChannels('');
    channelsFetchedRef.current = false;
  };

  const handleSeriesChange = (
    type: string,
    tagType: string | null,
    assessmentId: string | null
  ): void => {
    setSeriesType(type);
    setSeriesTagType(tagType);
    setSeriesAssessmentId(assessmentId);
  };

  const handleDownloadReport = async (type: string): Promise<void> => {
    try {
      setLoading(true);
      const data = await getReports(
        type,
        range,
        undefined,
        filterChannels,
        start_date,
        end_date,
        undefined,
        null,
        seriesType,
        seriesTagType,
        seriesAssessmentId
      );

      if (data) {
        if (data.url) {
          window.open(data.url);
        } else {
          alert('Error downloading the report. Please try again.');
        }
        setLoading(false);
      } else {
        console.error('No data returned from getReports.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error downloading the report: ', error);
      setLoading(false);
    }
  };

  // Show account selection screen if no account is selected
  if (!selectedAccount) {
    return (
      <SweetLayout>
        <div className="page-content">
          <Container fluid="xxl">
            <div style={{ marginTop: '40px' }}>
              <Row>
                <Col md={8} lg={6} className="mx-auto">
                  <div
                    style={{
                      background: '#ffffff',
                      padding: '32px',
                      borderRadius: '12px',
                      boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
                    }}
                  >
                    <h2 style={{ marginBottom: '24px', fontSize: '24px', fontWeight: '600' }}>
                      Reporting Dashboard
                    </h2>
                    <p style={{ marginBottom: '24px', color: '#6b7280', fontSize: '14px' }}>
                      Select an account to view reporting data and analytics
                    </p>
                    
                    <div style={{ marginBottom: '16px' }}>
                      <label
                        style={{
                          display: 'block',
                          marginBottom: '8px',
                          fontWeight: '600',
                          fontSize: '14px',
                          color: '#374151',
                        }}
                      >
                        Account
                      </label>
                      <Select
                        id="select-account"
                        onChange={handleAccountChange}
                        value={selectedAccount}
                        options={(accounts || []).map((account) => ({
                          value: account.account_id,
                          label: `${account.account_id} - ${account.name} - ${account.owner_user_id}`,
                        }))}
                        isSearchable
                        placeholder="Select an account..."
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '42px',
                            borderColor: '#D1D5DB',
                          }),
                        }}
                        isLoading={loadingAccounts}
                      />
                    </div>
                    
                    {loadingAccounts && (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                        Loading accounts...
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          </Container>
        </div>
      </SweetLayout>
    );
  }

  // Show full reporting interface once account is selected
  return (
    <SweetLayout>
      <div className="page-content">
        <Container fluid>
          <div className="page-header">
            <h1 className="page-title">Reporting</h1>
          </div>
        </Container>
        <Container fluid="xxl">
          <div className="reporting-page">
            {/* Account Selection at the top */}
            <Row className="mb-3">
              <Col>
                <div
                  style={{
                    background: '#f9fafb',
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                  }}
                >
                  <label
                    style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: '600',
                      fontSize: '14px',
                      color: '#374151',
                    }}
                  >
                    Account
                  </label>
                  <Select
                    id="select-account"
                    onChange={handleAccountChange}
                    value={selectedAccount}
                    options={(accounts || []).map((account) => ({
                      value: account.account_id,
                      label: `${account.account_id} - ${account.owner_user_id}`,
                    }))}
                    isSearchable
                    placeholder="Select an account..."
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '42px',
                        borderColor: '#D1D5DB',
                      }),
                    }}
                  />
                </div>
              </Col>
            </Row>

            {/* Compact Top-Level Controls */}
            <Row className="mb-3">
              <Col>
                <CompactReportingControls
                  range={range}
                  setRange={setRange}
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                  activeTab={activeTab}
                  loading={loading}
                  handleDownloadReport={handleDownloadReport}
                />
              </Col>
            </Row>

            {/* Tabbed Content */}
            <Tabs
              activeKey={activeTab}
              onSelect={k => setActiveTab(k || 'responses')}
              className="mb-3 reporting-tabs"
              mountOnEnter
              unmountOnExit
            >
              {/* Responses Tab */}
              <Tab eventKey="responses" title="Responses">
                <Row className="mb-3">
                  <Col>
                    <div
                      className="series-tag-filters"
                      style={{
                        background: '#f9fafb',
                        padding: '16px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                      }}
                    >
                      <div
                        style={{
                          marginBottom: seriesType === 'channels' ? '16px' : '0',
                        }}
                      >
                        <h6
                          style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            marginBottom: '12px',
                            color: '#374151',
                          }}
                        >
                          Series (Stack/Group By)
                        </h6>
                        <p
                          style={{
                            fontSize: '12px',
                            color: '#6b7280',
                            marginBottom: '12px',
                          }}
                        >
                          Stack data by channels, tag types, or assessments to compare trends
                        </p>
                        <SeriesDropdown
                          onSeriesChange={handleSeriesChange}
                          placeholder="Select series to stack data..."
                          ready={responsesReady}
                          selectedAccount={selectedAccount?.value as number || null}
                        />
                      </div>

                      {/* Channel Filter - Controls vary by series type */}
                      {seriesType === 'channels' && (
                        <div style={{ marginTop: '16px' }}>
                          <h6
                            style={{
                              fontSize: '14px',
                              fontWeight: 600,
                              marginBottom: '12px',
                              color: '#374151',
                            }}
                          >
                            Filter Channels
                          </h6>
                          <p
                            style={{
                              fontSize: '12px',
                              color: '#6b7280',
                              marginBottom: '12px',
                            }}
                          >
                            Select specific channels to display in the chart
                          </p>
                          <Select
                            id="select-channels-responses"
                            onChange={selectedOptions =>
                              handleChannelChange(selectedOptions as ChannelOption[])
                            }
                            value={channels}
                            options={(channel_list || []).map(channel => ({
                              value: channel.channel,
                              label: channel.channel + ' - ' + channel.name,
                            }))}
                            isSearchable
                            placeholder="All Channels"
                            isMulti
                          />

                          {/* Advanced Filters UI */}
                          <ChannelAdvancedFilters
                            value={advancedFiltersDraft}
                            onChange={setAdvancedFiltersDraft}
                            ready={responsesReady}
                            onApply={() => setAdvancedFilters(advancedFiltersDraft)}
                            showChannelFilters={false}
                            showTagFilters={true}
                            showAssessmentFilters={true}
                            selectedAccount={selectedAccount?.value as number || null}
                          />
                        </div>
                      )}
                      {seriesType === 'tags' && (
                        <div style={{ marginTop: '16px' }}>
                          <ChannelAdvancedFilters
                            value={advancedFiltersDraft}
                            onChange={setAdvancedFiltersDraft}
                            ready={responsesReady}
                            onApply={() => setAdvancedFilters(advancedFiltersDraft)}
                            showChannelFilters={true}
                            showTagFilters={false}
                            showAssessmentFilters={true}
                            channels={channels}
                            channelOptions={(channel_list || []).map(ch => ({
                              value: ch.channel,
                              label: ch.channel + ' - ' + ch.name,
                            }))}
                            onChannelsChange={opts => handleChannelChange(opts as any)}
                            selectedAccount={selectedAccount?.value as number || null}
                          />
                        </div>
                      )}
                      {seriesType === 'assessments' && (
                        <div style={{ marginTop: '16px' }}>
                          <ChannelAdvancedFilters
                            value={advancedFiltersDraft}
                            onChange={setAdvancedFiltersDraft}
                            ready={responsesReady}
                            onApply={() => setAdvancedFilters(advancedFiltersDraft)}
                            showChannelFilters={true}
                            showTagFilters={true}
                            showAssessmentFilters={false}
                            channels={channels}
                            channelOptions={(channel_list || []).map(ch => ({
                              value: ch.channel,
                              label: ch.channel + ' - ' + ch.name,
                            }))}
                            onChannelsChange={opts => handleChannelChange(opts as any)}
                            selectedAccount={selectedAccount?.value as number || null}
                          />
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <ResponseCountChart
                      getReports={getReports}
                      channel={filterChannels}
                      start_date={start_date}
                      end_date={end_date}
                      range={range}
                      setLoading={setLoading}
                      seriesType={seriesType}
                      seriesTagType={seriesTagType}
                      seriesAssessmentId={seriesAssessmentId}
                      advancedFilters={advancedFilters}
                      onInitialLoadComplete={() => setResponsesReady(true)}
                    />
                  </Col>
                </Row>
              </Tab>

              {/* Conversations Tab */}
              <Tab eventKey="conversations" title="Conversations">
                <Row className="mb-3">
                  <Col>
                    <div
                      style={{
                        background: '#f9fafb',
                        padding: '16px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                      }}
                    >
                      <div>
                        <h6
                          style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            marginBottom: '12px',
                            color: '#374151',
                          }}
                        >
                          Filter Channels
                        </h6>
                        <p
                          style={{
                            fontSize: '12px',
                            color: '#6b7280',
                            marginBottom: '12px',
                          }}
                        >
                          Select specific channels to display conversation counts
                        </p>
                        <Select
                          id="select-channels-conversations"
                          onChange={selectedOptions =>
                            handleChannelChange(selectedOptions as ChannelOption[])
                          }
                          value={channels}
                          options={(channel_list || []).map(channel => ({
                            value: channel.channel,
                            label: channel.channel + ' - ' + channel.name,
                          }))}
                          isSearchable
                          placeholder="All Channels"
                          isMulti
                        />
                      </div>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <ConversationCountChart
                      getReports={getReports}
                      channel={filterChannels}
                      start_date={start_date}
                      end_date={end_date}
                      range={range}
                      setLoading={setLoading}
                    />
                  </Col>
                </Row>
              </Tab>

              {/* Feedback Tab */}
              <Tab eventKey="feedback" title="Feedback">
                <Row className="mb-3">
                  <Col>
                    <div
                      style={{
                        background: '#f9fafb',
                        padding: '16px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                      }}
                    >
                      <div>
                        <h6
                          style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            marginBottom: '12px',
                            color: '#374151',
                          }}
                        >
                          Filter Channels
                        </h6>
                        <p
                          style={{
                            fontSize: '12px',
                            color: '#6b7280',
                            marginBottom: '12px',
                          }}
                        >
                          Select specific channels to display feedback data
                        </p>
                        <Select
                          id="select-channels-feedback"
                          onChange={selectedOptions =>
                            handleChannelChange(selectedOptions as ChannelOption[])
                          }
                          value={channels}
                          options={(channel_list || []).map(channel => ({
                            value: channel.channel,
                            label: channel.channel + ' - ' + channel.name,
                          }))}
                          isSearchable
                          placeholder="All Channels"
                          isMulti
                        />
                      </div>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <ActivitiesChart
                      getReports={getReports}
                      channel={filterChannels}
                      start_date={start_date}
                      end_date={end_date}
                      range={range}
                      setLoading={setLoading}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <UserFeedbackChart
                      getReports={getReports}
                      channel={filterChannels}
                      start_date={start_date}
                      end_date={end_date}
                      range={range}
                      setLoading={setLoading}
                      handleDownloadReport={handleDownloadReport}
                    />
                  </Col>
                </Row>
              </Tab>
            </Tabs>
          </div>
        </Container>
      </div>
    </SweetLayout>
  );
};

export default Reporting;

